<template>
  <div class="min-h-screen bg-theme-bg2 py-4 sm:py-8 lg:py-12 px-3 sm:px-6 lg:px-8 font-sans">
    <div class="max-w-5xl mx-auto space-y-6">
      <!-- Header Section -->
      <div class="bg-gradient-to-r from-primary-400 to-primary-600 text-white rounded-2xl shadow-lg p-5 sm:p-6 lg:p-8 text-center">
        <h2 class="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-wide">Orders Manager</h2>
        <p class="mt-2 text-sm sm:text-base text-white/80">Effortlessly manage your orders and track rentals.</p>
      </div>

      <!-- Filters Section -->
      <div class="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
        <select
          v-model="selectedStatus"
          @change="filterOrders"
          class="px-3 py-2.5 bg-theme-card text-theme-text border border-theme-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-auto text-sm transition"
          aria-label="Filter orders by status"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rented">Rented</option>
          <option value="pickup">Pickup Initiated</option>
          <option value="return">Return Initiated</option>
          <option value="rejected">Rejected</option>
          <option value="canceled">Canceled</option>
          <option value="completed">Completed</option>
          <option value="returned">Returned</option>
          <option value="partially_returned">Partially Returned</option>
        </select>
        <input
          v-model="searchQuery"
          @input="filterOrders"
          type="text"
          placeholder="Search by Order ID"
          class="px-3 py-2.5 bg-theme-card text-theme-text border border-theme-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-auto text-sm placeholder-theme-muted transition"
          aria-label="Search orders by ID"
        />
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
        <p class="mt-3 text-theme-text2 text-sm">Loading orders...</p>
      </div>

      <!-- Orders Content -->
      <template v-else-if="filteredOrders.length > 0">
        <div class="space-y-4">
          <div
            v-for="order in filteredOrders"
            :key="order.id"
            class="rounded-xl border border-theme-border shadow-sm overflow-hidden bg-theme-card"
          >
            <!-- Order Summary Header -->
            <div class="px-4 sm:px-6 py-3 sm:py-4 bg-theme-bg2/50 border-b border-theme-border">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                <!-- Left: ID + Date + Status -->
                <div class="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <span class="text-sm sm:text-base font-bold text-theme-text">#{{ order.id }}</span>
                  <span class="hidden sm:inline text-theme-muted">·</span>
                  <span class="text-[10px] sm:text-xs text-theme-muted">{{ formatDate(order.date_created) }}</span>
                  <span
                    :class="getStatusStyles(order.status)"
                    class="px-2.5 py-0.5 rounded-full capitalize text-[10px] sm:text-xs font-semibold"
                  >
                    {{ order.status.replace('_', ' ') }}
                  </span>
                </div>

                <!-- Right: Totals + Terminate -->
                <div class="flex items-center gap-3 sm:gap-4">
                  <span class="text-xs text-theme-text2">{{ order.total_order_items }} item(s)</span>
                  <span class="text-sm font-bold text-theme-text">{{ formatPrice(order.order_total_price) }}</span>
                  <button
                    v-if="['pending', 'approved', 'rented'].includes(order.status)"
                    @click="openTerminateModal(order.id)"
                    :disabled="isActionLoading[order.id]"
                    class="ml-auto sm:ml-0 px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    aria-label="Terminate order"
                  >
                    {{ isActionLoading[order.id] ? 'Terminating...' : 'Terminate' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Order Items -->
            <div class="divide-y divide-theme-border/50">
              <div
                v-for="item in order.order_items"
                :key="item.id"
                class="px-4 sm:px-6 py-3 hover:bg-theme-bg2/20 transition-colors"
              >
                <div class="flex items-start sm:items-center gap-3">
                  <!-- Image -->
                  <img
                    v-if="item.item.images?.length"
                    :src="item.item.images[0].image_url"
                    alt="Item image"
                    class="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover border border-theme-border shrink-0"
                  />
                  <div
                    v-else
                    class="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-theme-bg2 flex items-center justify-center shrink-0 border border-theme-border"
                  >
                    <i class="pi pi-image text-theme-muted text-sm"></i>
                  </div>

                  <!-- Item details -->
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3">
                      <!-- Name -->
                      <p class="text-sm font-medium text-theme-text truncate">
                        {{ item.quantity }}x {{ item.item.name }}
                      </p>

                      <!-- Status + Actions -->
                      <div class="flex items-center gap-2 shrink-0 flex-wrap">
                        <span
                          :class="getStatusStyles(item.status)"
                          class="px-2 py-0.5 text-[10px] rounded-full capitalize font-semibold whitespace-nowrap"
                        >
                          {{ item.status.replace('_', ' ') }}
                        </span>
                        <button
                          v-if="item.status === 'approved'"
                          @click="openPickupModal(item.id)"
                          class="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-xs font-medium whitespace-nowrap"
                          aria-label="Initiate pickup"
                        >
                          Pickup
                        </button>
                        <button
                          v-if="item.status === 'rented' && isPastEndDate(item.end_date)"
                          @click="openReturnModal(item.id)"
                          class="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-xs font-medium whitespace-nowrap"
                          aria-label="Initiate return"
                        >
                          Return
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div
        v-else
        class="flex flex-col items-center justify-center mx-auto bg-theme-card rounded-xl border border-theme-border shadow-sm p-6 sm:p-8 lg:p-12 text-center max-w-md"
      >
        <i class="pi pi-box text-4xl sm:text-5xl text-theme-muted mb-4"></i>
        <p class="text-base sm:text-lg text-theme-text">Nothing rented yet.</p>
        <p class="text-sm text-theme-text2 mt-1">Browse our catalog to find the equipment you need.</p>
        <RouterLink
          :to="{ name: 'categories' }"
          class="inline-block mt-5 px-6 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition text-sm"
        >
          I am looking for...
        </RouterLink>
      </div>

      <!-- ==================== Pickup Modal ==================== -->
      <div
        v-if="showPickupModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-3 sm:px-4"
      >
        <div class="bg-theme-card rounded-2xl shadow-2xl border border-theme-border w-full max-w-[90vw] sm:max-w-md lg:max-w-lg p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
          <h2 class="text-base sm:text-lg lg:text-xl font-semibold text-theme-text mb-4">
            Initiate Pickup for OrderItem #{{ currentItemId }}
          </h2>

          <label for="documentType" class="block mb-2 text-xs sm:text-sm font-medium text-theme-text2">
            Identity Document Type:
          </label>
          <select
            v-model="documentType"
            id="documentType"
            class="mb-2 px-3 py-2.5 border rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-theme-input text-theme-text border-theme-border text-sm transition"
          >
            <option value="">Select Document Type</option>
            <option value="passport">Passport</option>
            <option value="dl">Driver License</option>
            <option value="id">National ID</option>
          </select>

          <p v-if="!documentType && formSubmitted" class="text-red-500 text-xs mb-4">
            Document type is required.
          </p>

          <div class="mb-4 relative">
            <video
              ref="videoElement"
              class="w-full h-auto rounded-lg border border-theme-border"
              playsinline
              autoplay
              muted
            ></video>
            <canvas ref="canvasElement" class="hidden"></canvas>
            <div
              v-if="cameraAccessDenied"
              class="absolute inset-0 bg-theme-bg2 rounded-lg flex items-center justify-center text-theme-muted text-sm"
            >
              Camera access denied
            </div>
          </div>

          <button
            @click="captureImage"
            :class="[
              'w-full px-4 py-2.5 rounded-lg shadow-sm font-medium transition text-sm',
              cameraAccessDenied || capturedImages.length >= 3
                ? 'bg-primary-400 text-white cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            ]"
            :disabled="cameraAccessDenied || capturedImages.length >= 3"
          >
            {{ capturedImages.length >= 3 ? 'Maximum 3 images' : 'Capture Image' }}
          </button>

          <div class="mt-4">
            <h3 class="text-xs sm:text-sm font-medium text-theme-text2 mb-2">
              Captured Images ({{ capturedImages.length }}/3):
            </h3>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="(image, index) in capturedImages"
                :key="index"
                class="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border border-theme-border"
              >
                <img
                  :src="image"
                  :alt="`Captured document image ${index + 1}`"
                  class="w-full h-full object-cover"
                />
                <button
                  @click.stop="removeImage(index)"
                  class="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
                >
                  &times;
                </button>
              </div>
            </div>
          </div>

          <p v-if="capturedImages.length === 0 && formSubmitted" class="text-red-500 text-xs mb-4">
            At least one image is required.
          </p>

          <div class="flex flex-col sm:flex-row justify-between mt-6 gap-3">
            <button
              @click="submitPickup"
              :disabled="isSubmitting"
              :class="[
                'flex-1 px-4 py-2.5 rounded-lg shadow-sm font-medium transition text-sm',
                isSubmitting
                  ? 'bg-primary-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              ]"
            >
              {{ isSubmitting ? 'Submitting...' : 'Submit Pickup' }}
            </button>
            <button
              @click="handlePickupClose"
              class="flex-1 px-4 py-2.5 bg-theme-bg2 text-theme-text rounded-lg shadow-sm font-medium hover:bg-theme-bg2/80 transition text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- ==================== Return Modal ==================== -->
      <div
        v-if="showReturnModal"
        class="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-3 sm:px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="return-modal-title"
      >
        <div class="bg-theme-card p-5 sm:p-6 rounded-2xl shadow-2xl border border-theme-border w-full max-w-[90vw] sm:max-w-md lg:max-w-lg">
          <h2
            id="return-modal-title"
            class="text-base sm:text-lg lg:text-xl font-semibold mb-4 text-center text-theme-text"
          >
            Initiate Return
          </h2>
          <p class="text-sm mb-6 text-center text-theme-text2">
            Are you sure you want to initiate the return for this item?
          </p>
          <div class="flex flex-col sm:flex-row justify-between gap-3">
            <button
              @click="closeReturnModal"
              class="w-full py-2.5 px-4 bg-theme-bg2 text-theme-text font-semibold rounded-lg hover:bg-theme-bg2/80 transition text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Cancel return process"
            >
              Cancel
            </button>
            <button
              @click="initiateReturn"
              :disabled="isActionLoading[currentItemId]"
              class="w-full py-2.5 px-4 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 disabled:bg-primary-400"
              aria-label="Confirm return process"
            >
              {{ isActionLoading[currentItemId] ? 'Processing...' : 'Confirm' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ==================== Termination Modal ==================== -->
      <div
        v-if="showTerminateModal"
        class="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-3 sm:px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terminate-modal-title"
      >
        <div class="bg-theme-card p-5 sm:p-6 rounded-2xl shadow-2xl border border-theme-border w-full max-w-[90vw] sm:max-w-md lg:max-w-lg">
          <h2
            id="terminate-modal-title"
            class="text-base sm:text-lg lg:text-xl font-semibold mb-4 text-center text-theme-text"
          >
            Terminate Order
          </h2>
          <p class="text-sm mb-4 text-center text-theme-text2">
            Please provide a brief reason for terminating this order. This helps us keep things fair and transparent
            for both parties.
          </p>

          <label for="terminateReason" class="block text-sm font-medium text-theme-text2 mb-1">
            Termination reason
          </label>
          <textarea
            id="terminateReason"
            v-model="terminateReason"
            rows="4"
            class="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-theme-input text-theme-text border-theme-border transition"
            placeholder="Example: Renter did not show up, scheduling conflict, incorrect dates, etc."
          ></textarea>

          <p
            v-if="terminateFormSubmitted && !terminateReason.trim()"
            class="text-red-500 text-xs mt-1"
          >
            A reason is required to terminate this order.
          </p>

          <div class="flex flex-col sm:flex-row justify-between gap-3 mt-6">
            <button
              @click="closeTerminateModal"
              class="w-full py-2.5 px-4 bg-theme-bg2 text-theme-text font-semibold rounded-lg hover:bg-theme-bg2/80 transition text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              @click="submitTermination"
              :disabled="!terminateTargetOrderId || isActionLoading[terminateTargetOrderId]"
              class="w-full py-2.5 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {{ isActionLoading[terminateTargetOrderId] ? 'Terminating...' : 'Confirm Termination' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import useNotifications from '@/store/notification.js';
import { useRouter } from 'vue-router';
import { useCurrency } from '@/composables/useCurrency';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('Orders');

const { showNotification } = useNotifications();
const router = useRouter();
const { formatPrice } = useCurrency();
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const orders = ref([]);
const filteredOrders = ref([]);
const searchQuery = ref('');
const selectedStatus = ref('');
const loading = ref(true);

// Pickup modal
const showPickupModal = ref(false);
const showReturnModal = ref(false);
const currentItemId = ref(null);
const isActionLoading = ref({});

// Pickup Modal State
const documentType = ref('');
const capturedImages = ref([]);
const cameraAccessDenied = ref(false);
const cameraStream = ref(null);
const videoElement = ref(null);
const canvasElement = ref(null);
const formSubmitted = ref(false);
const isSubmitting = ref(false);

// Termination modal state
const showTerminateModal = ref(false);
const terminateReason = ref('');
const terminateTargetOrderId = ref(null);
const terminateFormSubmitted = ref(false);

// Fetch orders on mount
const fetchOrders = async () => {
  try {
    const response = await axios.get(`${apiBaseUrl}/api/orders/`, {
      withCredentials: true,
    });
    orders.value = response.data;
    filteredOrders.value = response.data;
    filterOrders();
  } catch (error) {
    logger.error('Error fetching orders', error);
    showNotification('Error', 'Failed to load orders. Please try again.', 'error');
    filteredOrders.value = orders.value;
  } finally {
    loading.value = false;
  }
};

// Status priority: lower number = needs attention first
const statusPriority = {
  pending: 0,
  approved: 1,
  pickup: 2,
  rented: 3,
  return: 4,
  partially_returned: 5,
  returned: 6,
  disputed: 7,
  completed: 8,
  rejected: 9,
  canceled: 10,
};

// Filter orders
const filterOrders = debounce(() => {
  filteredOrders.value = orders.value
    .filter((order) => {
      return (
        (selectedStatus.value ? order.status === selectedStatus.value : true) &&
        (searchQuery.value
          ? order.id.toString().toLowerCase().includes(searchQuery.value.toLowerCase())
          : true)
      );
    })
    .sort((a, b) => {
      const priorityA = statusPriority[a.status] ?? 99;
      const priorityB = statusPriority[b.status] ?? 99;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(b.date_created) - new Date(a.date_created);
    });
}, 300);

// Format date
const formatDate = (date) => {
  try {
    return format(new Date(date), 'Ppp');
  } catch (error) {
    return 'Invalid Date';
  }
};

// Status styles
const getStatusStyles = (status) => ({
  'bg-yellow-500/15 text-yellow-600': status === 'pending',
  'bg-green-500/15 text-green-600': status === 'pickup',
  'bg-blue-500/15 text-blue-600': status === 'approved',
  'bg-orange-500/15 text-orange-600': status === 'return',
  'bg-emerald-500/15 text-emerald-600': status === 'rented',
  'bg-red-500/15 text-red-600': status === 'rejected',
  'bg-theme-bg2 text-theme-muted': status === 'canceled',
  'bg-primary-500/15 text-primary-600': status === 'completed',
  'bg-purple-500/15 text-purple-600': status === 'returned',
  'bg-indigo-500/15 text-indigo-600': status === 'partially_returned',
  'bg-red-500/20 text-red-600': status === 'disputed',
});

// Check if end date is past
const isPastEndDate = (endDate) => {
  try {
    const date = new Date(endDate);
    if (isNaN(date.getTime())) return false;
    return date < new Date();
  } catch (error) {
    return false;
  }
};

// Generic actions (still usable for other future actions if needed)
const performAction = async (orderId, action) => {
  isActionLoading.value[orderId] = true;
  try {
    const response = await axios.post(
      `${apiBaseUrl}/api/orders/${orderId}/${action}/`,
      {},
      { withCredentials: true }
    );
    showNotification('Success', `Order ${action}d successfully!`, 'success');
    await fetchOrders();
  } catch (error) {
    logger.error(`Error performing ${action}`, error);
    showNotification(
      'Error',
      error.response?.data?.error || `Failed to ${action} order.`,
      'error'
    );
  } finally {
    isActionLoading.value[orderId] = false;
  }
};

// ---- Termination handlers ----
const openTerminateModal = (orderId) => {
  terminateTargetOrderId.value = orderId;
  terminateReason.value = '';
  terminateFormSubmitted.value = false;
  showTerminateModal.value = true;
};

const closeTerminateModal = () => {
  showTerminateModal.value = false;
  terminateTargetOrderId.value = null;
  terminateReason.value = '';
  terminateFormSubmitted.value = false;
};

const submitTermination = async () => {
  terminateFormSubmitted.value = true;
  if (!terminateReason.value.trim()) {
    showNotification('Error', 'Please provide a reason for terminating this order.', 'error');
    return;
  }

  const orderId = terminateTargetOrderId.value;
  if (!orderId) {
    showNotification('Error', 'Invalid order selected for termination.', 'error');
    return;
  }

  isActionLoading.value[orderId] = true;
  try {
    await axios.post(
      `${apiBaseUrl}/api/orders/${orderId}/terminate/`,
      { reason: terminateReason.value.trim() },
      { withCredentials: true }
    );
    showNotification('Success', 'Order terminated successfully.', 'success');
    closeTerminateModal();
    await fetchOrders();
  } catch (error) {
    logger.error('Error terminating order', error);
    showNotification(
      'Error',
      error.response?.data?.error || 'Failed to terminate order.',
      'error'
    );
  } finally {
    isActionLoading.value[orderId] = false;
  }
};

// ---- Pickup Modal Handlers ----
const startCamera = async (retryCount = 3) => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Camera API not supported');
    }
    stopCamera();
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });
    cameraStream.value = stream;
    cameraAccessDenied.value = false;
    if (videoElement.value) {
      videoElement.value.srcObject = stream;
    }
  } catch (error) {
    logger.error('Camera error', error);
    if (retryCount > 0) {
      setTimeout(() => startCamera(retryCount - 1), 1000);
    } else {
      cameraAccessDenied.value = true;
      showNotification(
        'Error',
        'Camera access denied. Please check permissions and try again.',
        'error'
      );
    }
  }
};

const stopCamera = () => {
  if (cameraStream.value) {
    cameraStream.value.getTracks().forEach((track) => track.stop());
    cameraStream.value = null;
  }
  if (videoElement.value && videoElement.value.srcObject) {
    videoElement.value.srcObject = null;
  }
};

const captureImage = () => {
  if (cameraAccessDenied.value || capturedImages.value.length >= 3) return;
  try {
    const canvas = canvasElement.value;
    const video = videoElement.value;
    if (!canvas || !video || !video.videoWidth || !video.videoHeight) {
      throw new Error('Camera not ready');
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL('image/jpeg', 0.85);
    capturedImages.value.push(imageData);
  } catch (error) {
    logger.error('Error capturing image', error);
    showNotification('Error', 'Failed to capture image. Please try again.', 'error');
  }
};

const removeImage = (index) => {
  capturedImages.value.splice(index, 1);
};

const submitPickup = async () => {
  formSubmitted.value = true;
  if (!documentType.value) {
    showNotification('Error', 'Please select a document type.', 'error');
    return;
  }
  if (capturedImages.value.length === 0) {
    showNotification('Error', 'Please capture at least one image.', 'error');
    return;
  }
  isSubmitting.value = true;
  try {
    const formData = new FormData();
    const imageBlobs = await Promise.all(
      capturedImages.value.map(async (image) => {
        const response = await fetch(image);
        if (!response.ok) throw new Error('Failed to process image');
        return await response.blob();
      })
    );
    imageBlobs.forEach((blob, index) => {
      formData.append('pickup_images', blob, `pickup_image_${index}.jpg`);
    });
    formData.append('documentType', documentType.value);
    formData.append('order_item_id', currentItemId.value);

    await axios.post(
      `${apiBaseUrl}/api/order-items/${currentItemId.value}/initiate_pickup/`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      }
    );
    showNotification('Success', 'Pickup initiated successfully!', 'success');
    handlePickupClose();
  } catch (error) {
    logger.error('Pickup submission error', error);
    showNotification(
      'Error',
      error.response?.data?.message || 'Failed to initiate pickup. Please try again.',
      'error'
    );
  } finally {
    isSubmitting.value = false;
  }
};

const openPickupModal = (itemId) => {
  currentItemId.value = itemId;
  showPickupModal.value = true;
  documentType.value = '';
  capturedImages.value = [];
  formSubmitted.value = false;
  isSubmitting.value = false;
  cameraAccessDenied.value = false;
  startCamera();
};

const handlePickupClose = async () => {
  stopCamera();
  showPickupModal.value = false;
  currentItemId.value = null;
  capturedImages.value = [];
  documentType.value = '';
  await fetchOrders();
};

// ---- Return Modal Handlers ----
const initiateReturn = async () => {
  if (!currentItemId.value) {
    showNotification('Error', 'Invalid order item.', 'error');
    return;
  }
  isActionLoading.value[currentItemId.value] = true;
  try {
    await axios.post(
      `${apiBaseUrl}/api/order-items/${currentItemId.value}/initiate_return/`,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    showNotification('Success', 'Return initiated successfully!', 'success');
    handleReturnInitiated();
  } catch (error) {
    logger.error('Return initiation failed', error);
    showNotification(
      'Error',
      error.response?.data?.message || 'Error initiating return.',
      'error'
    );
  } finally {
    isActionLoading.value[currentItemId.value] = false;
  }
};

const openReturnModal = (itemId) => {
  currentItemId.value = itemId;
  showReturnModal.value = true;
};

const closeReturnModal = () => {
  showReturnModal.value = false;
  currentItemId.value = null;
};

const handleReturnInitiated = async () => {
  closeReturnModal();
  await fetchOrders();
};

// Initialize
onMounted(() => {
  fetchOrders();
});

onUnmounted(() => {
  stopCamera();
});
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
